import { Link, useNavigate, useRouteLoaderData } from "react-router-dom";
import { ZonedDateTime } from "@js-joda/core";
import { Modal          } from "react-bootstrap";
import { showDate, showInstantLocal      } from "./Data/Date";
import { actingAsAtom, authAtom, SpotSyncAuth } from "./Data/Atoms";
import { useAtom, useAtomValue } from "jotai";
import { useEffect, useMemo, useState } from "react";
import { Input } from "./Controls/Input";
import { Button } from "./Controls/Button";
import { Alphabet, Asterisk, At, ExclamationTriangleFill, PersonFill } from "react-bootstrap-icons";
import { add } from "dinero.js";
import { cents, showMoneyShort, toCents } from "./Data/Money";
import { FacilityParkerBalance } from "./Data/ApiTypes";
import { PageTitle } from "./Controls/PageTitle";
import { SmallPage } from "./Controls/SmallPage";
import { HeaderText } from "./Controls/HeaderText";
import { UserPageDeleteAccountModal } from "./UserPageDeleteAccountModal";
import { Api } from "./Data/Api";
import { ChangeEmailReqApi, ChangePasswordReqApi } from "./Data/ApiTransport";
import { parseParker } from "./Data/ApiParse";

interface ChangeNameProps {
    show: boolean;
    setShow: (show: boolean) => void;
}

export function ChangeNameModal( { show, setShow }: ChangeNameProps ) {
    const [auth,  setAuth ] = useAtom( authAtom );
    const [name,  setName ] = useState( "" );
    const [error, setError] = useState<string | null>( null );

    const handleSubmit = () => {
        Api.changeName( { name: name } )
            .then( res => {
                if( res.isOk ) {
                    const parker = parseParker( res.value );
                    setAuth( { isLoggedIn: true, account: parker  } );
                    setShow( false );
                } else {
                    setError( res.error );
                }
            } )
            .catch( err => {
                console.error( 'API call failed:', err );
                setError( 'An unexpected error occurred. Please try again.' );
            } );
    };

    return (
        <Modal centered show={show} onEscapeKeyDown={() => setShow(false)}>
            <Modal.Header>
                <HeaderText>Change Name</HeaderText>
            </Modal.Header>
            <Modal.Body>
                <div>
                    <Input
                        type="text"
                        icon={<Alphabet />}
                        label="Enter Your Full Name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                    />
                </div>
                {error && <div className="text-danger">{error}</div>}
                <div className="text-end d-flex flex-row justify-content-end">
                    <Button className="w-50 m-2" onClick={() => setShow(false)}>
                        Cancel
                    </Button>
                    <Button className="w-50 m-2" onClick={handleSubmit}>
                        Change
                    </Button>
                </div>
            </Modal.Body>
        </Modal>
    );
}

interface ChangeEmailModalProps {
    show: boolean;
    setShow: (show: boolean) => void;
}

export function ChangeEmailModal( { show, setShow }: ChangeEmailModalProps ) {
    const [auth, setAuth] = useAtom( authAtom );
    const [form, setForm] = useState<ChangeEmailReqApi>( {
        currentPassword: '',
        newEmailAddress: '',
    } );

    const [error, setError] = useState<string | null>( null );
    const handleChange = ( e: React.ChangeEvent<HTMLInputElement> ) => {
        setForm( { ...form, [e.target.name]: e.target.value } );
    };

    const handleSubmit = () => {
        setError( null );
        Api.changeEmailAddress( form )
            .then( res => {
                if( res.isOk ) {
                    const parker = parseParker( res.value );
                    setAuth( { isLoggedIn: true, account: parker  } );
                    setShow( false );
                } else {
                    setError( res.error );
                }
            } )
            .catch( err => {
                console.error( 'API call failed:', err );
                setError( 'An unexpected error occurred. Please try again.' );
            } );
    };

    return (
        <Modal centered show={show} onEscapeKeyDown={() => setShow( false )}>
            <Modal.Header>
                <HeaderText>Change Email Address</HeaderText>
            </Modal.Header>
            <Modal.Body>
                <div>
                    <Input
                        type="password"
                        icon={<Asterisk />}
                        label="Enter Current Password"
                        name="currentPassword"
                        value={form.currentPassword}
                        onChange={handleChange}
                    />
                </div>
                <div>
                    <Input
                        type="email"
                        icon={<At />}
                        label="Enter New Email Address"
                        name="newEmailAddress"
                        value={form.newEmailAddress}
                        onChange={handleChange}
                    />
                </div>
                {error && <div className="text-danger">{error}</div>}
                <div className="text-end d-flex flex-row justify-content-end">
                    <Button className="w-50 m-2" onClick={() => setShow( false )}>
                        Cancel
                    </Button>
                    <Button className="w-50 m-2" onClick={handleSubmit}>
                        Change
                    </Button>
                </div>
            </Modal.Body>
        </Modal>
    );
}

interface ChangePasswordModalProps {
    show: boolean;
    setShow: (show: boolean) => void;
}

export function ChangePasswordModal( { show, setShow }: ChangePasswordModalProps ) {
    const [form, setForm] = useState<ChangePasswordReqApi>( {
        currentPassword: '',
        newPassword: '',
    } );
    const [error, setError] = useState<string | null>( null );
    const handleChange = ( e: React.ChangeEvent<HTMLInputElement> ) => {
        setForm( { ...form, [e.target.name]: e.target.value } );
    };

    const handleSubmit = () => {
        setError( null );
        Api.changePassword( form )
            .then( res => {
                if( res.isOk ) {
                    setShow( false );
                } else {
                    setError( res.error );
                }
            } )
            .catch( err => {
                console.error( 'API call failed:', err );
                setError( 'An unexpected error occurred. Please try again.' );
            } );
    };

    return (
        <Modal centered show={show} onEscapeKeyDown={() => setShow( false )}>
            <Modal.Header>
                <HeaderText>Change Password</HeaderText>
            </Modal.Header>
            <Modal.Body>
                <div>
                    <Input
                        type="password"
                        icon={<Asterisk />}
                        label="Enter Current Password"
                        name="currentPassword"
                        value={form.currentPassword}
                        onChange={handleChange}
                    />
                </div>
                <div>
                    <Input
                        type="password"
                        icon={<Asterisk />}
                        label="Enter New Password"
                        name="newPassword"
                        value={form.newPassword}
                        onChange={handleChange}
                    />
                </div>
                {error && <div className="text-danger">{error}</div>}
                <div className="text-end d-flex flex-row justify-content-end">
                    <Button className="w-50 m-2" onClick={() => setShow( false )}>
                        Cancel
                    </Button>
                    <Button className="w-50 m-2" onClick={handleSubmit}>
                        Change
                    </Button>
                </div>
            </Modal.Body>
        </Modal>
    );
}

export function UserProfilePage() {
    const nav = useNavigate();
    const [auth, setAuth] = useAtom( authAtom );
    const actingAs = useAtomValue( actingAsAtom );
    const [showChgEmail, setShowChgEmail] = useState( false );
    const [showChgPass,  setShowChgPass ] = useState( false );
    const [showChgName,  setShowChgName ] = useState( false );
    const [showDel,      setShowDel     ] = useState( false );
    const balances = ( useRouteLoaderData( "/user" ) as FacilityParkerBalance[] | undefined ) ?? [];
    const owedBalances = useMemo( () => balances.filter( b => toCents( b.balance ) > 0 ), [balances] );
    const totalDebt = useMemo(
        () => owedBalances.map( b => b.balance ).reduce( ( a, b ) => add( a, b ), cents( 0 ) ),
        [owedBalances]
    );
    const payUrl = owedBalances.length === 1
        ? `/user/${owedBalances[0].facility.facilityId}/pay`
        : "/user/order";

    useEffect( () => {
        if( !auth.account || !auth.isLoggedIn ) { nav( "/" ); return; }
        if( actingAs ) { nav( "/user/plan" ); }
    }, [auth, actingAs, nav] );

    if( !auth.account || !auth.isLoggedIn || actingAs ) return null;

    return <>
        <ChangeNameModal
            show={showChgName}
            setShow={setShowChgName} />

        <ChangeEmailModal
            show={showChgEmail}
            setShow={setShowChgEmail} />

        <ChangePasswordModal
            show={showChgPass}
            setShow={setShowChgPass} />

        <UserPageDeleteAccountModal
            show={showDel}
            setShow={setShowDel} />

        <PageTitle>
            Overview
        </PageTitle>
        {toCents( totalDebt ) > 0 && (
            <div className="rounded-3 border border-primary bg-white px-3 py-2 mb-3 d-flex align-items-start gap-2 w-100">
                <ExclamationTriangleFill className="text-primary flex-shrink-0 mt-1" />
                <div className="small">
                    You have an outstanding balance of {showMoneyShort( totalDebt )}.{" "}
                    <Link to={payUrl}>Pay now</Link>
                </div>
            </div>
        )}
        <SmallPage>
        <div className="column-gap-4" style={{
                display:             "grid",
                gridTemplateColumns: "minmax( max-content, 1fr) 1fr minmax( max-content, 1fr )" }}>
            <div className="grid-col-1 text-secondary">
                Name
            </div>
            <div className="grid-col-1 fw-bold mb-3">
                {auth.account.name}
            </div>
            <div className="grid-col-3 mb-3">
                <Link onClick={() => setShowChgName( true )} to={""}>Change</Link>
            </div>
            <div className="grid-col-1 text-secondary">
                Email
            </div>
            <div className="grid-col-1 fw-bold">
                {auth.account.emailAddress}
            </div>
            <div className="grid-col-3 mb-3">
                <Link onClick={() => setShowChgEmail( true )} to={""}>Change</Link>
            </div>
            <div className="grid-col-1 text-secondary">
                Password
            </div>
            <div className="grid-col-1 fw-bold">
                ***********
            </div>
            <div className="grid-col-3 mb-3">
                <Link onClick={() => setShowChgPass( true )} to={""}>Change</Link>
            </div>
            <div className="grid-col-1 text-secondary">
                Account Created
            </div>
            <div className="grid-col-1 mb-3">
                {showInstantLocal( auth.account.createdWhen )}
            </div>
        </div>
        </SmallPage>
    </>;
}